import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Palette, MessageSquareText, FileCheck2, Check, MessageCircle, Upload } from "lucide-react";
import { Reveal } from "./Reveal";

const STEPS = [
  {
    key: "personalize",
    n: "01",
    icon: Palette,
    title: "Personalize",
    tag: "Sua marca, uma vez só",
    desc: "Envie sua logo, dados da empresa, escolha as cores e o modelo A4. O Basiq guarda tudo por empresa — você pode ter várias.",
  },
  {
    key: "peca",
    n: "02",
    icon: MessageSquareText,
    title: "Peça",
    tag: "No Basiq ou no WhatsApp",
    desc: "Descreva o serviço do seu jeito, em uma frase. A IA interpreta, pergunta só o que faltar e monta o orçamento na hora.",
  },
  {
    key: "receba",
    n: "03",
    icon: FileCheck2,
    title: "Receba",
    tag: "PDF pronto em alta",
    desc: "Baixe o PDF em alta resolução e envie ao cliente na hora, com a sua marca aplicada.",
  },
];

const Preview = ({ step }) => {
  if (step === "personalize") {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-14 h-14 rounded-xl border-2 border-dashed border-slate-200 grid place-items-center text-slate-400"><Upload size={18} /></div>
          <div className="flex-1 space-y-1.5"><div className="h-2.5 w-32 rounded-full bg-slate-200" /><div className="h-2 w-20 rounded-full bg-slate-100" /></div>
        </div>
        <div className="flex items-center gap-2">
          {["#0f172a", "#4A3BFA", "#059669", "#b45309", "#e11d48"].map((c, i) => (
            <span key={c} className="w-8 h-8 rounded-full ring-1 ring-black/5 grid place-items-center" style={{ background: c }}>{i === 1 && <Check size={14} className="text-white" strokeWidth={3} />}</span>
          ))}
        </div>
        <div className="grid grid-cols-3 gap-2">
          {[0, 1, 2].map((i) => (<div key={i} className={`h-16 rounded-lg ring-1 ${i === 0 ? "ring-slate-900" : "ring-slate-200"} bg-white`} />))}
        </div>
      </div>
    );
  }
  if (step === "peca") {
    return (
      <div className="space-y-3">
        <div className="inline-flex items-center gap-1.5 text-[11px] font-medium text-[#128C7E] bg-[#25D366]/10 rounded-full px-2.5 py-1"><MessageCircle size={13} /> WhatsApp conectado</div>
        <div className="ml-auto max-w-[80%] bg-slate-900 text-white rounded-2xl rounded-tr-md px-3.5 py-2.5 text-[13px]">Orçamento de logo pro cliente João, R$ 900, entrega em 10 dias</div>
        <div className="max-w-[80%] bg-slate-50 ring-1 ring-slate-100 rounded-2xl rounded-tl-md px-3.5 py-2.5 text-[13px] text-slate-600">Prontinho! Montei seu orçamento. 📄 Quer ajustar algo?</div>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <div className="rounded-xl ring-1 ring-slate-200 p-3 bg-white">
        <div className="flex items-center justify-between"><div className="h-2.5 w-24 rounded-full bg-slate-200" /><span className="text-[9px] font-semibold uppercase tracking-wider text-slate-400">Orçamento</span></div>
        <div className="mt-3 space-y-1.5">{[0, 1, 2].map((i) => (<div key={i} className="flex justify-between"><div className="h-2 rounded-full bg-slate-100" style={{ width: `${60 - i * 8}%` }} /><div className="h-2 w-12 rounded-full bg-slate-100" /></div>))}</div>
      </div>
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center gap-1.5 text-[12px] font-medium text-white bg-slate-900 rounded-full px-3 py-1.5"><FileCheck2 size={14} /> Baixar PDF</span>
        <span className="inline-flex items-center gap-1.5 text-[12px] font-medium text-emerald-700 bg-emerald-50 ring-1 ring-emerald-200 rounded-full px-3 py-1.5"><Check size={14} /> Pronto para enviar</span>
      </div>
    </div>
  );
};

export default function HowItWorks() {
  const [active, setActive] = useState("personalize");
  return (
    <section id="como-funciona" className="relative px-5 py-20 sm:py-28">
      <div className="max-w-6xl mx-auto">
        <Reveal className="text-center max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider bg-slate-100 text-slate-600 mb-4">Como funciona</div>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Três passos. Zero complicação.</h2>
          <p className="mt-4 text-slate-600 text-base sm:text-lg">Configure uma vez e gere quantos orçamentos quiser — pelo app ou pelo WhatsApp.</p>
        </Reveal>

        <div className="mt-14 grid lg:grid-cols-2 gap-8 items-start">
          {/* steps list */}
          <div className="space-y-3">
            {STEPS.map((s, i) => {
              const on = active === s.key;
              return (
                <Reveal key={s.key} delay={i * 0.08}>
                  <button
                    onClick={() => setActive(s.key)}
                    data-testid={`step-${s.key}`}
                    className={`w-full text-left rounded-2xl p-5 sm:p-6 transition-all duration-300 ${on ? "bg-white ring-2 ring-slate-900 shadow-[0_16px_40px_-16px_rgba(15,23,42,0.2)]" : "bg-white/60 ring-1 ring-slate-200 hover:ring-slate-300"}`}
                  >
                    <div className="flex items-start gap-4">
                      <span className={`grid place-items-center w-11 h-11 rounded-xl shrink-0 transition-colors ${on ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-500"}`}><s.icon size={20} /></span>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-mono-jb text-[12px] text-slate-400">{s.n}</span>
                          <h3 className="text-lg font-semibold text-slate-900">{s.title}</h3>
                          <span className="text-[11px] font-medium text-slate-400">· {s.tag}</span>
                        </div>
                        <AnimatePresence initial={false}>
                          {on && (
                            <motion.p initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.3 }} className="text-[14px] text-slate-600 leading-relaxed mt-1.5 overflow-hidden">
                              {s.desc}
                            </motion.p>
                          )}
                        </AnimatePresence>
                      </div>
                    </div>
                  </button>
                </Reveal>
              );
            })}
          </div>

          {/* live preview */}
          <Reveal delay={0.15} className="lg:sticky lg:top-24">
            <div className="rounded-3xl bg-white ring-1 ring-slate-200 shadow-[0_24px_60px_-30px_rgba(15,23,42,0.25)] p-6 sm:p-8 min-h-[320px] flex flex-col justify-center">
              <AnimatePresence mode="wait">
                <motion.div key={active} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}>
                  <Preview step={active} />
                </motion.div>
              </AnimatePresence>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
